import React, {useCallback, useContext, useMemo} from 'react';

import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {ValidationContext} from 'src/contexts/validation-context';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';

import {FlatButton} from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';


import {DomainView} from './domain-view';
import {DomainsSkeleton} from './domains-skeleton';

import type {ValidationContextType} from 'src/contexts/validation-context';
import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {Error} from 'src/openapi/admin/models/Error';

type Props = {
    busyDomains: Record<string, boolean>,
    isLoading: boolean,
    loadingError: Error | null,
    domains: Array<string>,
    onChange: React.Dispatch<React.SetStateAction<Array<string>>>,
    setLastEditedDomain: (id: number) => void,

    disabled?: boolean,
    readonly?: boolean,
};

const SECTION_NAME = 'Account domains';

export const DomainsSection: React.FC<Props> = props => {
    const {
        isLoading,
        loadingError,
        busyDomains,
        domains,
        onChange,
        setLastEditedDomain,
        disabled,
        readonly,
    } = props;
    const {removeError, getErrorsByControlId} = useContext<ValidationContextType>(ValidationContext);

    const onAddDomain = useCallback(() => {
        onChange(prevDomains => [...prevDomains, '']);
    }, [onChange]);

    const onChangeDomain = useCallback((id: number, value: string) => {
        onChange(prevDomains => prevDomains.map((domain, i) => (i === id ? value : domain)));
        removeError(`account-domain-${id}`);
        setLastEditedDomain(id);
    }, [onChange, removeError, setLastEditedDomain]);

    const onRemoveDomain = useCallback((id: number) => {
        onChange(prevDomains => prevDomains.filter((domain, i) => i !== id));
        removeError(`account-domain-${id}`);
    }, [onChange, removeError]);

    const disableAddDomains = useMemo(
        () => disabled || readonly || domains.some((domain, i) => {
            const hasError = !!getErrorsByControlId(`account-domain-${i}`).length;
            return hasError || !domain;
        }),
        [domains, getErrorsByControlId, disabled, readonly],
    );

    return (
        <Skeleton view={<DomainsSkeleton/>} shown={isLoading}>
            <ServerResponseHandler
                smallView={true}
                sectionName={SECTION_NAME}
                serverError={loadingError}
                accessRight={ACCESS_RIGHTS.ACCOUNT_SCREEN_VIEW as AccessRight}>
                <WidthItem width={700}>
                    <Label>{SECTION_NAME}</Label>
                    <Group alignItems="stretch" direction="v" gap="s">
                        {domains.map((accessDomain, i) => (
                            <DomainView
                                key={i}
                                id={i}
                                disabled={disabled}
                                readonly={readonly}
                                busy={busyDomains[i]}
                                totalCount={domains.length}
                                accessDomain={accessDomain}
                                onChange={onChangeDomain}
                                onRemove={onRemoveDomain}
                            />
                        ))}
                        <WidthItem width="100">
                            <FlatButton
                                disabled={disableAddDomains}
                                startIcon={<Icon type={Icons.PLUS_CIRCLE}/>}
                                onClick={onAddDomain}>
                                <b>Add Domain</b>
                            </FlatButton>
                        </WidthItem>
                    </Group>
                </WidthItem>
            </ServerResponseHandler>
        </Skeleton>
    );
};
